import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import type { GetSessionsDto } from '@/types/dtos/sessions/response/get-sessions.dto.ts';
import type { SessionType } from '@/types/dtos/sessions/session-type.enum';
import useEventMode from '@/components/events/hooks/use-event-mode';
import type { Mode } from '@/types/entity-mode';

export const GET_EVENT_FILTERED_SESSIONS_QUERY_KEY = 'get-event-filtered-sessions';
const PAGE_SIZE = '100';

export const useEventFilteredSessionsQuery = ({
  eventId,
  type,
  isInLibrary,
}: {
  eventId?: string;
  type?: SessionType;
  isInLibrary?: boolean;
}) => {
  const { getToken } = useAuth();
  const mode = useEventMode();

  return useInfiniteQuery({
    queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY, eventId, mode, type || null],
    queryFn: async ({ pageParam }) => {
      const token = await getToken();
      return getEventSessions({ eventId: eventId!, type, isInLibrary, mode, pageParam, token: token ?? '' });
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor,
    enabled: !!eventId,
    placeholderData: (previousData) => previousData,
  });
};

const getEventSessions = async ({
  eventId,
  type,
  mode,
  isInLibrary,
  pageParam,
  token,
}: {
  eventId: string;
  pageParam: string;
  token: string;
  type?: SessionType;
  mode?: Mode;
  isInLibrary?: boolean;
}): Promise<GetSessionsDto> => {
  const searchParams = new URLSearchParams({ limit: PAGE_SIZE });
  if (pageParam !== '') {
    searchParams.append('cursor', pageParam);
  }
  if (type) {
    searchParams.append('type', type);
  }
  if (isInLibrary) {
    searchParams.append('isInLibrary', isInLibrary.toString());
  }
  const remappedMode = mode === 'PERFORM' ? 'ORGANIZE' : mode;
  if (remappedMode) {
    searchParams.append('mode', remappedMode);
  }
  const url = `${import.meta.env.VITE_API_URL}/sessions/event/${eventId}?${searchParams.toString()}`;
  return sendJSONRequest({ url, method: METHOD.GET, token });
};
